import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../Redux/actions/authActions";
import { getProjects } from "../../Redux/actions/projectsActions";
import "../UserPlan/ChoosePlanStyle.css";

export default function Setting({
  isSettingOpen,
  setIsSettingOpen,
  anchorRef,
}) {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({ top: 68, right: 36 });

  const { _id, isCard } = useSelector((state) => state?.project) || {};
  const userInfo = JSON.parse(localStorage.getItem("user"));

  const { isDashboard, isMoreInfo } = location?.state || {};
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const pathname = pathSegments[0];

  useEffect(() => {
    if (!isSettingOpen) return;

    const updateDropdownPosition = () => {
      const anchor = anchorRef?.current;
      if (!anchor) {
        setDropdownStyle({ top: 68, right: 36 });
        return;
      }
      const rect = anchor.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom + 8,
        right: Math.max(window.innerWidth - rect.right, 16),
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [isSettingOpen, anchorRef]);

  useEffect(() => {
    if (!isSettingOpen) return;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (dropdownRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      setIsSettingOpen(false);
    };

    // Use click (not mousedown) so menu item onClick can run first
    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isSettingOpen, setIsSettingOpen, anchorRef]);

  const getUserInitials = () => {
    const firstName = userInfo?.firstName?.trim() || "";
    if (firstName) return firstName.charAt(0).toUpperCase();
    return "U";
  };

  const closeAndNavigate = (to, options) => {
    setIsSettingOpen(false);
    // Defer so the menu unmount doesn't cancel navigation
    window.setTimeout(() => {
      navigate(to, options);
    }, 0);
  };

  const logoutHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsSettingOpen(false);
    dispatch(logout(() => navigate("/")));
    dispatch(getProjects("logout"));
  };

  const navigateToBillingInfo = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (pathname === "more_info") return;

    const onDashboardRoot =
      pathname === "dashboard" && pathSegments.length === 1;

    if (onDashboardRoot || (isDashboard && isMoreInfo)) {
      closeAndNavigate("/more_info", { state: { isDashboard: true } });
      return;
    }

    closeAndNavigate("/more_info", {
      state: {
        isDashboard: false,
        isMoreInfo: true,
        projectId: _id || pathSegments[2],
        isCard: Boolean(isCard) || pathSegments[1] === "card",
      },
    });
  };

  if (!isSettingOpen) return null;

  return createPortal(
    <>
      <div
        className="cp-dropdown-overlay"
        style={{ zIndex: 10000 }}
        onClick={() => setIsSettingOpen(false)}
      />
      <div
        ref={dropdownRef}
        className="cp-profile-dropdown"
        style={{
          top: dropdownStyle.top,
          right: dropdownStyle.right,
          zIndex: 10001,
        }}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="cp-dropdown-header">
          <div className="cp-dropdown-avatar">{getUserInitials()}</div>
          <div className="cp-dropdown-user">
            <div className="cp-dropdown-name">
              {userInfo?.firstName} {userInfo?.lastName}
            </div>
            <div className="cp-dropdown-email">{userInfo?.email}</div>
          </div>
        </div>
        <div className="cp-dropdown-divider" />
        <div className="cp-dropdown-body">
          <button
            type="button"
            className="cp-dropdown-item"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              closeAndNavigate("/account-settings");
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            My Account
          </button>
          <button
            type="button"
            className="cp-dropdown-item"
            disabled={pathname === "more_info"}
            onClick={navigateToBillingInfo}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
            Billing Info
          </button>
          <button
            type="button"
            className="cp-dropdown-item"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsSettingOpen(false);
              window.open("/help/", "_blank", "noopener,noreferrer");
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Help Desk
          </button>
          <a
            href="https://www.scrollx.io/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="cp-dropdown-item"
            onClick={(event) => {
              event.stopPropagation();
              setIsSettingOpen(false);
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Contact us
          </a>
        </div>
        <div className="cp-dropdown-divider" />
        <div className="cp-dropdown-footer">
          <button
            type="button"
            className="cp-dropdown-item cp-danger"
            onClick={logoutHandler}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Log out
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
